import { styled } from "@linaria/react";
import Button from "@components/_Revamp/Button";
import { Tooltip } from "@pages/CCP/components/Tooltip";
import { ROUTE_PATH } from "@static/route-paths";
import { SCREEN_SIZE } from "@static/screen-size";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import HealthMainContainer from "@components/_Revamp/HealthMainContainer";
import { css } from "@linaria/core";
import InnerContainer from "@components/_Revamp/InnerContainer";
import { useAvivaSelector } from "@redux/hook";
import { useDispatch } from "react-redux";
import FlatButton from "@pages/CCP/components/FlatButton";
import { updateUnderWriting1 } from "@redux/slices/CCP/step3Slice";
import ErrorModal from "@pages/CCP/components/Modals/ErrorModal";
import { themeDefault } from "src/theme";
import { trackOnCTA, trackOnLoad } from "@pages/CCP/Helpers/analyticsHelper";
import { CCP_STEP_LIST } from "@static/content";
import { ContainerWithStepper } from "@pages/CCP/components/ContainerWithStepper";

const treatmentItems = [
  {
    text: "Adenomyosis",
    tooltipContent: (
      <span>
        A condition where the inner lining of uterus (called the endometrium)
        starts to grow into the muscular wall of the uterus (called the
        myometrium).
      </span>
    ),
  },
  {
    text: "Cervical cyst",
  },
  {
    text: "Chalazion",
    tooltipContent: (
      <span>
        <p>
          A red bump in the eyelid that forms due to blockage of an oil gland
          (called a meibomian gland).
        </p>
        <br />
        <p>Related term:</p>
        <p>- Meibomian cyst</p>
      </span>
    ),
  },
  {
    text: "Dermoid cyst",
    tooltipContent: (
      <span>
        A benign growth that can contain hair, sebaceous material, teeth, bone,
        and nerve tissue. It may be found on the skin of the face, scalp, neck,
        or upper chest.
      </span>
    ),
  },
  {
    text: "Endometriosis",
  },
  {
    text: "Keratinous cyst",
    tooltipContent: (
      <span>
        A benign growth that often originates from a hair follicle, usually pale
        and filled with lamellated keratin. It is commonly found on the face,
        scalp, neck, or back.
      </span>
    ),
  },
  {
    text: "Nabothian cyst",
    tooltipContent: (
      <span>
        A benign bump on the surface of the cervix or cervical canal. The cervix
        is lined with glands that normally secrete mucus and these endocervical
        glands can become filled with secretions that accumulate as a
        pimple-like elevation called Nabothian cysts.
      </span>
    ),
  },
  {
    text: "Sebaceous cyst",
    tooltipContent: (
      <span>
        A benign growth that arises within the skin on the face, neck, upper
        back, or upper chest. It can form when the opening to a sebaceous gland
        becomes blocked. The oily substance called sebum continues to be
        produced but cannot escape to the outer skin surface.
      </span>
    ),
  },
  {
    text: "Spinal synovial cyst",
  },
];

const recurrenceItems = [
  {
    text: "Congenital brain cyst (arachnoid/colloid)",
  },
  {
    text: "Endometrial polyp",
  },
  {
    text: "Gallbladder polyp",
  },
  {
    text: "Hemangioma",
    tooltipContent: (
      <span>
        An abnormal buildup of blood vessels in the skin, or internal organs
        like liver.
      </span>
    ),
  },
  {
    text: "Lipoma",
    tooltipContent: (
      <span>
        A benign, soft tumour that forms under the skin, made up of fatty
        tissue.
      </span>
    ),
  },
  {
    text: "Ovarian cyst",
  },
  {
    text: "Pilonidal cyst",
    tooltipContent: (
      <span>
        <p>
          An unusual pocket in the skin that occurs near the tailbone at the top
          of the buttocks. Pilonidal cyst develops as the sac which holds the
          hair follicle and skin debris starts to turn inflamed.
        </p>
        <br />
        <p>Related terms:</p>
        <p>- Pilonidal sinus</p>
        <p>- Pilonidal disease</p>
      </span>
    ),
  },
  {
    text: "Rhabdomyoma",
    tooltipContent: (
      <span>
        A very rare type of benign (non-cancerous) tumour that arises from
        striated muscle. It may be found in the heart, tongue, neck muscles,
        axilla, or vulva.
      </span>
    ),
  },
  {
    text: "Simple breast cyst",
  },
  {
    text: "Uterine fibroid",
  },
];

const breastGrowthsItems = [
  {
    text: "Fibroadenoma",
  },
  {
    text: "Fibrocystic breast disease",
  },
  {
    text: "Simple breast cyst which has not been removed",
  },
];

function UnderWriting1b() {
  const history = useHistory();
  const dispatch = useDispatch();
  const underWriting1 = useAvivaSelector(
    (state) => state.cancerCoverStep3.underWriting1
  );
  const basicInfo = useAvivaSelector(
    (state) => state.cancerCoverStep1.basicInfo
  );
  const [openModal, setOpenModal] = useState<boolean>();

  const onClickNext = useCallback(() => {
    if (underWriting1?.isBenignGrowths === "YES") {
      if (
        (underWriting1?.isTreatment === "YES" &&
          underWriting1?.isRecurrence === "NO" &&
          underWriting1?.isBreastGrowths === "NO" &&
          underWriting1?.isColonPolyps === "NO" &&
          underWriting1?.isOtherBenignGrowths === "NO") ||
        (underWriting1?.isTreatment === "NO" &&
          underWriting1?.isRecurrence === "YES" &&
          underWriting1?.isBreastGrowths === "NO" &&
          underWriting1?.isColonPolyps === "NO" &&
          underWriting1?.isOtherBenignGrowths === "NO") ||
        (underWriting1?.isTreatment === "YES" &&
          underWriting1?.isRecurrence === "YES" &&
          underWriting1?.isBreastGrowths === "NO" &&
          underWriting1?.isColonPolyps === "NO" &&
          underWriting1?.isOtherBenignGrowths === "NO")
      ) {
        history.push(ROUTE_PATH.CANCER_COVER_UW_2);
        trackOnCTA({ ctaText: "Next", ctaType: "qb:link" });
      } else {
        setOpenModal(true);
        return;
      }
    } else {
      dispatch(
        updateUnderWriting1({
          isTreatment: "NO",
          isRecurrence: "NO",
          isBreastGrowths: "NO",
          isColonPolyps: "NO",
          isOtherBenignGrowths: "NO",
        })
      );
      history.push(ROUTE_PATH.CANCER_COVER_UW_2);
      trackOnCTA({ ctaText: "Next", ctaType: "qb:link" });
    }
  }, [
    dispatch,
    history,
    underWriting1?.isBenignGrowths,
    underWriting1?.isBreastGrowths,
    underWriting1?.isColonPolyps,
    underWriting1?.isOtherBenignGrowths,
    underWriting1?.isRecurrence,
    underWriting1?.isTreatment,
  ]);

  //AA Page Loading
  useEffect(() => {
    trackOnLoad();
  }, []);

  return (
    <>
      <ContainerWithStepper
        currentStep={ROUTE_PATH.CANCER_COVER_UW_1A}
        backButtonPath={ROUTE_PATH.CANCER_COVER_UW_1A}
      >
        <InnerContainer className={underwritingContainerStyle}>
          <section className="quoteheader">
            <section className={premiumContainer}>
              <p>
                {basicInfo?.forwhom === "myself"
                  ? "Have you had any benign growths?"
                  : "Has your child had any benign growth?"}
              </p>
            </section>
          </section>
          <section className="actionContainer">
            <FlatButton
              onClick={() => {
                dispatch(updateUnderWriting1({ isBenignGrowths: "YES" }));
              }}
              maxWidth="100%"
              isActive={underWriting1?.isBenignGrowths === "YES"}
              className="flatBtn"
            >
              Yes
            </FlatButton>
            <FlatButton
              onClick={() => {
                dispatch(updateUnderWriting1({ isBenignGrowths: "NO" }));
              }}
              maxWidth="100%"
              isActive={underWriting1?.isBenignGrowths === "NO"}
              className="flatBtn"
            >
              No
            </FlatButton>
          </section>
        </InnerContainer>
        {underWriting1.isBenignGrowths === "YES" ? (
          <>
            <InnerContainer className={underwritingContainerStyle}>
              <section className="quoteheader">
                <section className={premiumContainer}>
                  <p>
                    {basicInfo?.forwhom === "myself"
                      ? "Have you had any of the following but were advised that no treatment or referral to a specialist was needed?"
                      : "Has your child had any of the following but been advised that no treatment or referral to a specialist was needed?"}
                  </p>
                </section>
              </section>
              <section className="quotecontent">
                {/* <BulletListing bulletList={textItems} rowGap={16} /> */}
                {treatmentItems.map((item) => {
                  return (
                    <p key={item.text} className="content-title">
                      <span>&#183;</span>
                      {item.tooltipContent ? (
                        <Tooltip title={item.text} isFixed={true}>
                          {item.tooltipContent}
                        </Tooltip>
                      ) : (
                        <p>{item.text}</p>
                      )}
                    </p>
                  );
                })}
              </section>
              <section className="actionContainer">
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isTreatment: "YES" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isTreatment === "YES"}
                  className="flatBtn"
                >
                  Yes, at least one
                </FlatButton>
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isTreatment: "NO" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isTreatment === "NO"}
                  className="flatBtn"
                >
                  No, none of the above
                </FlatButton>
              </section>
            </InnerContainer>
            <InnerContainer className={underwritingContainerStyle}>
              <section className="quoteheader">
                <section className={premiumContainer}>
                  <p>
                    {basicInfo?.forwhom === "myself"
                      ? "Have you had any of the following that was removed with no recurrence and follow-up needed?"
                      : "Has your child had any of the following that was removed with no recurrence and follow-up needed?"}
                  </p>
                </section>
              </section>
              <section className="quotecontent">
                {/* <BulletListing bulletList={textItems} rowGap={16} /> */}
                {recurrenceItems.map((item) => {
                  return (
                    <p key={item.text} className="content-title">
                      <span>&#183;</span>
                      {item.tooltipContent ? (
                        <Tooltip title={item.text} isFixed={true}>
                          {item.tooltipContent}
                        </Tooltip>
                      ) : (
                        <p>{item.text}</p>
                      )}
                    </p>
                  );
                })}
              </section>
              <section className="actionContainer">
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isRecurrence: "YES" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isRecurrence === "YES"}
                  className="flatBtn"
                >
                  Yes, at least one
                </FlatButton>
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isRecurrence: "NO" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isRecurrence === "NO"}
                  className="flatBtn"
                >
                  No, none of the above
                </FlatButton>
              </section>
            </InnerContainer>
            <InnerContainer className={underwritingContainerStyle}>
              <section className="quoteheader">
                <section className={premiumContainer}>
                  <p>
                    {basicInfo?.forwhom === "myself"
                      ? "Have you had any breast growths such as:"
                      : "Has your child had any breast growths such as:"}
                  </p>
                </section>
              </section>
              <section className="quotecontent">
                {/* <BulletListing bulletList={textItems} rowGap={16} /> */}
                {breastGrowthsItems.map((item: any) => {
                  return (
                    <p key={item.text} className="content-title">
                      <span>&#183;</span>
                      {item.tooltipContent ? (
                        <Tooltip title={item.text} isFixed={true}>
                          {item.tooltipContent}
                        </Tooltip>
                      ) : (
                        <p>{item.text}</p>
                      )}
                    </p>
                  );
                })}
              </section>
              <section className="actionContainer">
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isBreastGrowths: "YES" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isBreastGrowths === "YES"}
                  className="flatBtn"
                >
                  Yes, at least one
                </FlatButton>
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isBreastGrowths: "NO" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isBreastGrowths === "NO"}
                  className="flatBtn"
                >
                  No, none of the above
                </FlatButton>
              </section>
            </InnerContainer>
            <InnerContainer className={underwritingContainerStyle}>
              <section className="quoteheader">
                <section className={premiumContainer}>
                  <p>
                    {basicInfo?.forwhom === "myself"
                      ? "Have you had colon polyps which have been removed, with no recurrence and no further treatment and follow-up needed?"
                      : "Has your child had colon polyps which have been removed, with no recurrence and no further treatment and follow-up needed?"}
                  </p>
                </section>
              </section>
              <section className="actionContainer">
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isColonPolyps: "YES" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isColonPolyps === "YES"}
                  className="flatBtn"
                >
                  Yes
                </FlatButton>
                <FlatButton
                  onClick={() => {
                    dispatch(updateUnderWriting1({ isColonPolyps: "NO" }));
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isColonPolyps === "NO"}
                  className="flatBtn"
                >
                  No
                </FlatButton>
              </section>
            </InnerContainer>
            <InnerContainer className={underwritingContainerStyle}>
              <section className="quoteheader">
                <section className={premiumContainer}>
                  <p>
                    {basicInfo?.forwhom === "myself"
                      ? "Have you had any other benign growths?"
                      : "Has your child had any other benign growth?"}
                  </p>
                </section>
              </section>
              <section className="actionContainer">
                <FlatButton
                  onClick={() => {
                    dispatch(
                      updateUnderWriting1({ isOtherBenignGrowths: "YES" })
                    );
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isOtherBenignGrowths === "YES"}
                  className="flatBtn"
                >
                  Yes
                </FlatButton>
                <FlatButton
                  onClick={() => {
                    dispatch(
                      updateUnderWriting1({ isOtherBenignGrowths: "NO" })
                    );
                  }}
                  maxWidth="100%"
                  isActive={underWriting1?.isOtherBenignGrowths === "NO"}
                  className="flatBtn"
                >
                  No
                </FlatButton>
              </section>
            </InnerContainer>
          </>
        ) : null}
        <ButtonContianer>
          <Button
            variant="default"
            label="Next"
            onClick={onClickNext}
            fontSize="16px"
            disabled={
              underWriting1?.isBenignGrowths === null ||
              (underWriting1?.isBenignGrowths === "YES" &&
                Object.values(underWriting1).filter((v) => v === null).length >
                  0)
            }
          />
        </ButtonContianer>
      </ContainerWithStepper>
      {openModal && (
        <ErrorModal
          title="You may need some assistance"
          error="It looks like you might need assistance from a Financial Adviser Representative
              to find a more suitable plan for your needs."
          onCloseFn={() => setOpenModal(false)}
        />
      )}
    </>
  );
}

export default UnderWriting1b;

const underwritingContainerStyle = css`
  margin-bottom: 24px;
  padding: 24px;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    padding: 24px 64px 24px 64px;
  }
  .quoteheader {
    p {
      font-size: 18px;
      font-weight: 600;
      line-height: 25.2px;
      letter-spacing: 0.015em;
      text-align: left;
      margin-bottom: 36px;
    }
  }
  .subHeader {
    margin: 24px 0;
    p {
      font-size: 12px;
      font-weight: 600;
      line-height: 16.8px;
      letter-spacing: 0.015em;
      text-align: left;
      @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
        font-weight: 500;
      }
    }
  }
  .quotecontent {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    margin-bottom: 32px;
    .content-title {
      font-size: 14px;
      font-weight: 500;
      width: 100%;
      margin-left: 8px;
      display: flex;
      span {
        margin-right: 8px;
      }
      @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
        width: 45%;
        margin-left: 0px;
      }
    }
  }
  .actionContainer {
    display: flex;
    gap: 12px;
    flex-direction: column;
    @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
      flex-direction: row;
      gap: 8px;
    }
    .flatBtn {
      font-size: 14px;
      @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
        font-size: 12px;
      }
    }
  }
  .note {
    display: flex;
    flex-direction: column;
    margin-top: 36px;
    .header {
      font-size: 14px;
      font-weight: 500;
      line-height: 19.6px;
      letter-spacing: 0.015em;
      text-align: left;
      color: #434343;
    }
    textarea {
      margin: 16px 0 12px 0;
      border: 1px solid #D8D8D8;
      border-radius: 8px;
      height: 142px;
      padding: 16px;
      font-size: 14px;
      font-weight: 600;
    }
    .counter {
      font-size: 12px;
      font-weight: 600;
      line-height: 16.8px;
      letter-spacing: 0.015em;
      text-align: left;
      color: #c9c9c9;
      margin-left: auto;
    }
  }
`;

const premiumContainer = css`
  display: flex;
  font-size: 28px;
  font-weight: 600;
  justify-content: space-between;
  line-height: 39.2px;

  p {
    font-size: 18px;
    letter-spacing: 0.27px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    p {
      font-size: 28px;
    }
  }
`;

const ButtonContianer = styled.div`
  margin: auto;
  margin-top: 50px;
  width: 100%;
  max-width: 288px;
`;
